// In-App Notification Handler
// Handles push notifications when the app is open to show toast notifications

import { useEffect } from 'react';
import { useNotifications } from './NotificationProvider';

/**
 * In-App Notification Handler Component
 * Listens for push notifications and shows toasts when the app is open
 */
export function InAppNotificationHandler() {
  const { showInfo, showSuccess, showWarning } = useNotifications();

  useEffect(() => {
    // Listen for messages from service worker about push notifications
    const handleMessage = (event) => {
      console.log('[InAppNotificationHandler] Received message:', event.data);

      // Check if this is a push notification message
      if (event.data && event.data.type === 'PUSH_NOTIFICATION_RECEIVED') {
        const { title, body, data = {} } = event.data.notification;

        console.log('[InAppNotificationHandler] Processing push notification:', {
          title,
          body,
          data,
        });

        // Determine the type of notification based on the data
        let toastType = 'info';
        let toastMessage = body; // Default to just the body

        // Normalize notification type key (backend uses snake_case)
        const normalizedType = data.notificationType || data.notification_type || data.type;
        // Customize based on notification type from data
        if (normalizedType) {
          switch (normalizedType) {
            case 'session_reminder':
              toastType = 'info';
              toastMessage = `🚣 ${body}`;
              break;
            case 'schedule_change':
              toastType = 'warning';
              toastMessage = `📅 ${body}`;
              break;
            case 'crew_assignment':
              toastType = 'success';
              toastMessage = `👥 ${body}`;
              break;
            case 'admin_announcement':
              toastType = 'info';
              toastMessage = `📢 ${body}`;
              break;
            case 'schedule_reminder':
              toastType = 'warning';
              toastMessage = `⏰ ${body}`;
              break;
            case 'session_scheduled':
              toastType = 'success';
              toastMessage = `✅ ${body}`;
              break;
            case 'session_rescheduled':
              toastType = 'warning';
              toastMessage = `🔁 ${body}`;
              break;
            default:
              toastMessage = body;
          }
        } else {
          // If no specific type, try to infer from title or add emoji
          if (title && title.toLowerCase().includes('training')) {
            toastMessage = `🚣 ${body}`;
          } else if (title && title.toLowerCase().includes('schedule')) {
            toastType = 'warning';
            toastMessage = `📅 ${body}`;
          } else if (title && title.toLowerCase().includes('crew')) {
            toastType = 'success';
            toastMessage = `👥 ${body}`;
          } else {
            // Generic notification with title
            toastMessage = title ? `${title}: ${body}` : body;
          }
        }

        // Show the toast notification
        switch (toastType) {
          case 'success':
            showSuccess(toastMessage, 7000); // Show for 7 seconds
            break;
          case 'warning':
            showWarning(toastMessage, 8000); // Show for 8 seconds
            break;
          case 'info':
          default:
            showInfo(toastMessage, 6000); // Show for 6 seconds
            break;
        }

        console.log('[InAppNotificationHandler] Displayed toast for push notification:', {
          title,
          type: toastType,
          message: toastMessage,
        });
      }
    };

    // Register the message listener
    navigator.serviceWorker?.addEventListener('message', handleMessage);

    // Cleanup on unmount
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [showInfo, showSuccess, showWarning]);

  // This component doesn't render anything
  return null;
}

export default InAppNotificationHandler;
